import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import { Form, Row, Col, Radio, Tooltip, InputNumber } from 'antd'
import { InputMoney } from 'components'
import Select from 'components/Select/Select'
import TextAmortization from 'components/ModalInvite/TextPromoterCommission'
import { InfoCircleOutlined, LoadingOutlined, MinusCircleFilled, PlusCircleFilled } from '@ant-design/icons'
import format from 'utils/format'
import RefinancingButton from '../RefinancingButton/RefinancingButton'
import { IGetAvailableCreditValue } from 'egi/app/Simulator/components/SimulatorLoanInfoStep/SimulatorLoanInfoStep'
import swal from 'utils/swal'
import { simulation as consumerSimulation } from 'egi/consumers'
import { simulationLastStep, simulationPricingValues, simulationSetId, simulationValues } from 'store/modules/simulation/actions'
import { useDispatch } from 'react-redux'
import { useClient, useSimulation } from 'hooks'
import { _amortizationTypeEnum } from 'globals'
import { ISimulation } from 'egi/types'
import { simulationRepository } from 'egi/repositories/SimulationsRepository'
import ProposalEgiModel from 'egi/models/ProposalEgiModel'
import { invalidSimulationFieldsError } from 'components/InvalidFieldsError/InvalidFieldsError'
import { validateResponse } from 'utils/validate'

type IRefinancingDataValues = {
  deadline?: number
  outstandingBalance?: number
  resourceGoal: string
  amortizationType: string
  hasRegister?: boolean
  creditValue: number
  currentStep: number
  clientId?: string
  simulationId?: string
  index?: string
  product: 'home-equity'
}

export default function RefinancingData () {
  const [formRef] = Form.useForm()
  const simulation = useSimulation()
  const dispatch = useDispatch()
  const client = useClient()
  const inputRef: MutableRefObject<any> = useRef(null)
  const [availableCredit, setAvailableCredit] = useState<number | undefined>(0)
  const [hasRegister, setHasRegister] = useState<boolean | undefined>(true)
  const [amortizationType, setAmortizationType] = useState<string>('price')
  const [deadline, setDeadline] = useState<number>()
  const [creditValue, setCreditValue] = useState<number | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingAvailable, setLoadingAvailable] = useState<boolean>(false)
  const [errors, setErrors] = useState<any>({})

  async function getAvailableCreditValue ({ deadlineValue, amortization, register }: IGetAvailableCreditValue) {
    setLoadingAvailable(true)
    try {
      const data = {
        ...simulation.values,
        hasRegister: register,
        amortizationType: amortization || simulation.values?.amortizationType,
        currentStep: 2,
        deadline: deadlineValue || deadline,
        outstandingBalance: simulation.values?.outstandingBalance,
        index: simulation.lastStep?.index ?? undefined,
        simulationId: simulation.id || simulation.values?._id,
        product: 'home-equity'
      }

      const response = await simulationRepository.getAvailableCreditValue(data as Partial<ISimulation>)
      setAvailableCredit(response.data.data.maxCreditValue)
      setLoadingAvailable(false)
    } catch (err: any) {
      swal.basic({ title: 'Atenção!', text: err.message, icon: 'warning' })
      console.error(err)
      setLoadingAvailable(false)
    }
  }

  useEffect(() => {
    const fetchPricing = async () => {
      const data = {
        amortizationType: simulation.values?.amortizationType,
        birthdate: simulation.values?.birthdate,
        creditValue: simulation.values?.creditValue,
        deadline: simulation.values?.deadline,
        index: simulation.values?.index,
        personType: simulation.values?.personType,
        product: simulation.values?.product ?? 'home-equity',
        propertyValue: simulation.values?.propertyValue,
        sub: simulation.values?.sub
      }

      try {
        const response = await consumerSimulation.entries(data)
        const ranges = response.data.pricing
        dispatch(simulationPricingValues(ranges))
      } catch (err: any) {
        if (err?.code === 412) swal.htmlBasic({ icon: 'warning', title: 'Atenção', text: err.message })
        else swal.basic({ title: 'Atenção', text: err.message, icon: 'warning' })
        console.warn(err)
      }
    }

    if (simulation.values) fetchPricing()
  }, [simulation.values])

  useEffect(() => {
    function onFill () {
      const data = Object.assign({}, simulation.values) || {}
      setDeadline(simulation.pricing?.deadlineMax)

      if (data.amortizationType) {
        const lowerCaseAmortizationType = data.amortizationType.toLowerCase()
        setAmortizationType(lowerCaseAmortizationType)
        data.amortizationType = lowerCaseAmortizationType
      }

      if (data.creditValue) {
        setCreditValue(data.creditValue)
      }
      data.amortizationType = data.amortizationType?.toLowerCase() as _amortizationTypeEnum

      if (data.hasRegister !== false) {
        setHasRegister(true)
        data.hasRegister = 'yes'
      } else {
        setHasRegister(false)
        data.hasRegister = 'no'
      }

      if (data.propertyValue) {
        getAvailableCreditValue({
          deadlineValue: simulation?.pricing?.deadlineMax,
          register: data.hasRegister === 'yes' ? true : false
        })
      }

      if (formRef) formRef.setFieldsValue(data)
    }

    onFill()
  }, [simulation.values, simulation.pricing])

  useEffect(() => {
    if (creditValue === undefined && simulation?.values?.creditValue === undefined) setCreditValue(simulation?.pricing?.creditMin)
  }, [simulation?.pricing?.creditMin])

  const onSubmit = async (values: IRefinancingDataValues) => {
    const allSimulationValues = Object.assign({}, simulation.values)

    values.deadline = deadline

    if (creditValue) values.creditValue = format.formatMoneySend(String(creditValue))
    if (!values.amortizationType) values.amortizationType = amortizationType

    const filteredSimulationValues = { ...allSimulationValues }
    Reflect.deleteProperty(filteredSimulationValues, 'iof')
    Reflect.deleteProperty(filteredSimulationValues, 'liquid')

    const data: IRefinancingDataValues = {
      ...filteredSimulationValues,
      ...values,
      outstandingBalance: simulation.values?.outstandingBalance,
      clientId: client._id ?? simulation.values?.clientId,
      index: simulation.lastStep?.index,
      hasRegister: hasRegister,
      currentStep: 2,
      simulationId: simulation.id || simulation.values?._id,
      product: 'home-equity'
    }

    setLoading(true)
    setErrors({})

    try {
      const response = await consumerSimulation.simulation(data)
      const lastStepInfo = response.data.simulation
      const simulationId = response.data.simulation.simulationId
      if (!simulation.id) dispatch(simulationSetId(simulationId))

      dispatch(simulationLastStep(lastStepInfo, 2))
      dispatch(simulationValues({
        ...data,
        liquid: lastStepInfo.liquid
      }, 2))
    } catch (err: any) {
      if (err.data?.invalid) {
        const invalidsFields = validateResponse(err.data?.invalid)
        setErrors(ProposalEgiModel.factoryErrorMessage(invalidsFields))

        const mappedInvalids = ProposalEgiModel.mapErrorMessage([...err.data?.invalid])
        invalidSimulationFieldsError({
          invalidFields: mappedInvalids
        })

        return
      }

      setErrors({})
      swal.htmlBasic({ title: 'Atenção', text: err.message, icon: 'warning' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='refinancing-content'>
      <h4 className="simulator-title-baseline" style={{ fontSize: 18, marginBottom: 24 }}>
        Dados do Refinanciamento
      </h4>

      <Form
        form={formRef}
        layout="vertical"
        onFinish={onSubmit}
      >
        <Row gutter={[16, 16]}>
          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='propertyValue'
              label={<label className="simulator-label">Valor do imóvel</label>}
              className='refinancing-form-item'
              rules={[{ required: true, message: 'Digite um valor do imóvel válido' }]}
              required
            >
              <InputMoney
                id="income"
                className="simulator-input ant-input"
                placeholder='R$'
              />
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='resourceGoal'
              label={<label className="simulator-label">Finalidade do empréstimo</label>}
              className="refinancing-form-item simulation-resourceGoal"
              rules={[{ required: true, message: 'Selecione uma finalidade do empréstimo válida' }]}
              required
            >
              <Select
                id="test-select-simulation-resourceGoal"
                placeholder="Escolha uma opção"
                className="unauth-inputs-center"
              >
                <Select.Option id="test-select-simulation-resourceGoal-quitardividas" value="quitardividas">Quitar dívidas</Select.Option>
                <Select.Option id="test-select-simulation-resourceGoal-portabilidade" value="portabilidade">Portabilidade</Select.Option>
                <Select.Option id="test-select-simulation-resourceGoal-construcao" value="construcao">Reforma</Select.Option>
                <Select.Option id="test-select-simulation-resourceGoal-investimento" value="investimento">Investimento</Select.Option>
                <Select.Option id="test-select-simulation-resourceGoal-capitaldegiro" value="capitaldegiro">Capital de giro</Select.Option>
                <Select.Option id="test-select-simulation-resourceGoal-aquisicaodebens" value="aquisicaodebens">Aquisição de bens</Select.Option>
                <Select.Option id="test-select-simulation-resourceGoal-lazer" value="lazer">Viajar</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='amortizationType'
              className="refinancing-form-item amortizationType-radio refinancing-amortizationType"
              label={
                <label className="simulator-label user-select">
                  Sistema de amortização
                  <label className="pl-1">
                    <Tooltip
                      color="white"
                      placement="right"
                      destroyTooltipOnHide={{ keepParent: false }}
                      title={<TextAmortization />}
                    >
                      <InfoCircleOutlined className="simulation-info " />
                    </Tooltip>
                  </label>
                </label>
              }
              rules={[{ required: true, message: 'Escolha um sistema de amortização válido' }]}
              required
            >
              <Radio.Group
                className="w-100 simulator-radio"
                defaultValue={amortizationType}
                value={amortizationType}
                size="large"
              >
                <Radio.Button
                  className="position-relative simulation-amortization-tag"
                  value="price"
                  onClick={() => {
                    setAmortizationType('price')
                    getAvailableCreditValue({ amortization: 'price', register: hasRegister })
                  }}
                >
                  PRICE
                </Radio.Button>

                <Radio.Button
                  value="sac"
                  onClick={() => {
                    setAmortizationType('sac')
                    getAvailableCreditValue({ amortization: 'sac', register: hasRegister })
                  }}
                >
                  Tabela SAC
                </Radio.Button>
              </Radio.Group>

            </Form.Item>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='creditValue'
              label={<label className="simulator-label">Valor do empréstimo</label>}
              className="refinancing-form-item mb-0"
              rules={[{ required: true, message: 'Digite um valor do empréstimo válido' }]}
              required
              help={errors.creditValue && errors.creditValue}
              validateStatus={errors.creditValue && 'error'}
            >
              <div className="position-relative">
                <InputMoney
                  inputRef={inputRef}
                  defaultValue={creditValue}
                  value={creditValue}
                  id="creditValue"
                  className="simulator-input ant-input"
                  onChange={(value: any) => { setCreditValue(value) }}
                  placeholder="R$"
                />

                <span className="refinancing-tag-input user-select" onClick={() => inputRef.current.focus()}>
                  Valor mínimo: <label className="simulation-values-text">
                    {format.formatBRLMinAndMax(50000)}
                  </label>
                </span>
              </div>
            </Form.Item>

            <div className="text-center">
              <span className="color-primary">
                Valor de crédito disponível: <b>{loadingAvailable
                  ? <LoadingOutlined className="ml-2" />
                  : format.precisionFormatBRL(Number(availableCredit))
                }</b>
              </span>
            </div>
          </Col>

          <Col className="py-1" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='deadline'
              label={<label className="simulator-label">Quantidade de parcelas</label>}
              className="refinancing-form-item mb-0 form-slider-simulator"
              rules={[{ required: true, message: 'Digite uma quantidade de parcelas válida' }]}
              required
            >
              <div className="flex">
                <div className="flex flex-align-center flex-justify-center mr-3">
                  <MinusCircleFilled
                    disabled={Number(deadline) <= Number(simulation.pricing?.deadlineMin)}
                    onClick={() => {
                      if ((Number(deadline) - 6) >= Number(simulation.pricing?.deadlineMin)) {
                        setDeadline(Number(deadline) - 6)
                        getAvailableCreditValue({ deadlineValue: Number(deadline) - 6, register: hasRegister, amortization: amortizationType })
                      } else {
                        setDeadline(Number(simulation.pricing?.deadlineMin))
                        getAvailableCreditValue({ deadlineValue: Number(simulation.pricing?.deadlineMin), register: hasRegister, amortization: amortizationType })
                      }
                    }}
                    style={(deadline === Number(simulation.pricing?.deadlineMin))
                      ? { opacity: 0.5, userSelect: 'none' }
                      : { opacity: 1, userSelect: 'none' }
                    }
                    className='pointer simulator-deadline-increase'
                  />
                </div>

                <InputNumber
                  id="deadline"
                  className="w-100 mx-2 mx-md-0 simulator-input user-select"
                  value={Number(deadline) || simulation.pricing?.deadlineMax}
                  onChange={(item: string | number | null | undefined) => {
                    setDeadline(Number(item))
                  }}
                  onBlur={(e) => getAvailableCreditValue({ deadlineValue: Number(e.target.value), register: hasRegister, amortization: amortizationType })}
                  min={Number(simulation.pricing?.deadlineMin)}
                  max={Number(simulation.pricing?.deadlineMax)}
                />

                <div className="flex flex-align-center flex-justify-center ml-3">
                  <PlusCircleFilled
                    disabled={Number(deadline) >= Number(simulation.pricing?.deadlineMax)}
                    onClick={() => {
                      if ((Number(deadline) + 6) <= Number(simulation.pricing?.deadlineMax)) {
                        setDeadline(Number(deadline) + 6)
                        getAvailableCreditValue({ deadlineValue: Number(deadline) + 6, register: hasRegister, amortization: amortizationType })
                      } else {
                        setDeadline(Number(simulation.pricing?.deadlineMax))
                        getAvailableCreditValue({ deadlineValue: Number(simulation.pricing?.deadlineMax), register: hasRegister, amortization: amortizationType })
                      }
                    }}
                    style={(deadline === Number(simulation.pricing?.deadlineMax)) ? { opacity: 0.5, userSelect: 'none' } : { opacity: 1 }}
                    className='pointer simulator-deadline-increase'
                  />
                </div>
              </div>

              <div className="flex flex-justify-center my-3 user-select no-wrap-white-space">
                {!!Number(simulation.pricing?.deadlineMin) && (
                  <>
                    <h4 className="color-primary">Mínimo: {Number(simulation.pricing?.deadlineMin)} meses</h4> -
                    <h4 className="color-primary">Máximo: {Number(simulation.pricing?.deadlineMax)} meses</h4>
                  </>
                )}
              </div>
            </Form.Item>
          </Col>

          <Col className="py-3" lg={24} md={24} sm={24} xs={24}>
            <Form.Item
              name='hasRegister'
              valuePropName="checked"
              className="refinancing-form-item simulation-hasRegister"
              label={
                <label className="simulator-label">
                  Incluir custos de registro
                  <label className="pl-1">
                    <Tooltip
                      color="white"
                      placement="right"
                      destroyTooltipOnHide={{ keepParent: false }}
                      title={
                        <p className="simulator-tooltip-paragraph mb-0">
                          O custo de registro é direcionado para pagamento das despesas de cartório
                          para a efetivação do imóvel como garantia do empréstimo, sendo descontado
                          do crédito a liberar.
                        </p>
                      }
                    >
                      <InfoCircleOutlined className="simulation-info" />
                    </Tooltip>
                  </label>
                </label>
              }
              rules={[{ required: true, message: 'Escolha se deseja incluir custos de registro' }]}
              required
            >
              <Radio.Group
                className="w-100 simulator-radio"
                defaultValue="yes"
                size="large"
              >
                <Radio.Button
                  value="yes"
                  id="simulation-has-register"
                  className="w-25"
                  onClick={() => {
                    getAvailableCreditValue({ register: true, amortization: amortizationType })
                    setHasRegister(true)
                  }}
                >
                  Desejo incluir
                </Radio.Button>

                <Radio.Button
                  value="no"
                  id="simulation-not-has-register"
                  onClick={() => {
                    getAvailableCreditValue({ register: false, amortization: amortizationType })
                    setHasRegister(false)
                  }}
                >
                  Não desejo incluir
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <RefinancingButton
          loading={loading}
          handleClick={() => formRef.submit()}
        />
      </Form>
    </div>
  )
}
